
import React, { useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import {
  useGetBillingSettingsQuery,
  useUpdateBillingSettingsMutation,
  useEnableBillingSettingsMutation,
  useDisableBillingSettingsMutation,
  useDisableDiscountMutation,
  useDeleteBillingSettingsMutation,
} from "../../slices/coachingCenterBillingApiSlice";

const CoachingCenterBillingSettingsScreen = () => {
  const navigate = useNavigate();

  const { coachingCenterId } = useParams();

  // =====================================================
  // API
  // =====================================================

  const { data, isLoading, error, refetch } = useGetBillingSettingsQuery(
    coachingCenterId,
    {
      skip: !coachingCenterId,
    },
  );

  const [updateBillingSettings, { isLoading: isUpdating }] =
    useUpdateBillingSettingsMutation();

  const [enableBillingSettings, { isLoading: isEnabling }] =
    useEnableBillingSettingsMutation();

  const [disableBillingSettings, { isLoading: isDisabling }] =
    useDisableBillingSettingsMutation();

  const [disableDiscount, { isLoading: isDisablingDiscount }] =
    useDisableDiscountMutation();

  const [deleteBillingSettings, { isLoading: isDeleting }] =
    useDeleteBillingSettingsMutation();

  const coachingCenter = data?.data?.coachingCenter;
  const settings = data?.data?.settings;

  // =====================================================
  // Local State
  // =====================================================

  const [isActive, setIsActive] = useState(true);

  const [discountEnabled, setDiscountEnabled] = useState(false);

  const [discountType, setDiscountType] = useState("percentage");

  const [discountValue, setDiscountValue] = useState("");

  const [maxAmount, setMaxAmount] = useState("");

  const [reason, setReason] = useState("");

  const [validFrom, setValidFrom] = useState("");

  const [validUntil, setValidUntil] = useState("");

  const [message, setMessage] = useState("");

  const [messageType, setMessageType] = useState("success");

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  const [showDiscountDisableConfirm, setShowDiscountDisableConfirm] =
    useState(false);

  // =====================================================
  // Populate Form
  // =====================================================

  useEffect(() => {
    if (!settings) return;

    setIsActive(Boolean(settings.isActive));

    const discount = settings.discount || {};

    setDiscountEnabled(Boolean(discount.enabled));

    setDiscountType(discount.type || "percentage");

    setDiscountValue(
      discount.value !== null && discount.value !== undefined
        ? String(discount.value)
        : "",
    );

    setMaxAmount(
      discount.maxAmount !== null && discount.maxAmount !== undefined
        ? String(discount.maxAmount)
        : "",
    );

    setReason(discount.reason || "");

    setValidFrom(formatDateForInput(discount.validFrom));

    setValidUntil(formatDateForInput(discount.validUntil));
  }, [settings]);

  // =====================================================
  // Helpers
  // =====================================================

  const formatDateForInput = (date) => {
    if (!date) return "";

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return "";
    }

    return parsed.toISOString().split("T")[0];
  };

  const formatDate = (date) => {
    if (!date) return "-";

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
      return "-";
    }

    return parsed.toLocaleDateString("en-IN");
  };

  const formatCurrency = (amount) => {
    return `₹${Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const showMessage = (text, type = "success") => {
    setMessage(text);
    setMessageType(type);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const clearMessage = () => {
    setMessage("");
  };

  // =====================================================
  // Validation
  // =====================================================

  const validateForm = () => {
    if (!discountEnabled) {
      return true;
    }

    const numericValue = Number(discountValue || 0);

    if (numericValue < 0) {
      showMessage("Discount value cannot be negative.", "error");

      return false;
    }

    if (discountType === "percentage" && numericValue > 100) {
      showMessage("Percentage discount cannot exceed 100%.", "error");

      return false;
    }

    if (maxAmount !== "" && Number(maxAmount) < 0) {
      showMessage("Maximum discount amount cannot be negative.", "error");

      return false;
    }

    if (validFrom && validUntil) {
      const from = new Date(validFrom);
      const until = new Date(validUntil);

      if (until < from) {
        showMessage(
          "Discount valid until date must be after the valid from date.",
          "error",
        );

        return false;
      }
    }

    return true;
  };

  // =====================================================
  // Save
  // =====================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    clearMessage();

    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        isActive,

        discount: {
          enabled: discountEnabled,

          type: discountType,

          value: discountEnabled ? Number(discountValue || 0) : 0,

          maxAmount:
            discountEnabled && discountType === "percentage" && maxAmount !== ""
              ? Number(maxAmount)
              : null,

          reason: discountEnabled && reason.trim() ? reason.trim() : null,

          validFrom: discountEnabled && validFrom ? validFrom : null,

          validUntil: discountEnabled && validUntil ? validUntil : null,
        },
      };
      console.log("=== BILLING UPDATE DEBUG ===");
console.log("coachingCenterId:", coachingCenterId);
console.log("update arguments:", {
  centerId: coachingCenterId,
  data: payload,
});
      await updateBillingSettings({
        coachingCenterId,
        data: payload,
      }).unwrap();

      showMessage("Billing settings updated successfully.", "success");

      refetch();
    } catch (err) {
      showMessage(
        err?.data?.message ||
          err?.error ||
          "Unable to update billing settings.",
        "error",
      );
    }
  };

  // =====================================================
  // Enable Billing
  // =====================================================

  const handleEnableBilling = async () => {
    clearMessage();

    try {
      await enableBillingSettings(coachingCenterId).unwrap();

      setIsActive(true);

      showMessage("Billing settings enabled successfully.", "success");

      refetch();
    } catch (err) {
      showMessage(
        err?.data?.message ||
          err?.error ||
          "Unable to enable billing settings.",
        "error",
      );
    }
  };

  // =====================================================
  // Disable Billing
  // =====================================================

  const handleDisableBilling = async () => {
    setShowDisableConfirm(false);

    clearMessage();

    try {
      await disableBillingSettings(coachingCenterId).unwrap();

      setIsActive(false);

      showMessage("Billing settings disabled successfully.", "success");

      refetch();
    } catch (err) {
      showMessage(
        err?.data?.message ||
          err?.error ||
          "Unable to disable billing settings.",
        "error",
      );
    }
  };

  // =====================================================
  // Disable Discount
  // =====================================================

  const handleDisableDiscount = async () => {
    setShowDiscountDisableConfirm(false);

    clearMessage();

    try {
      await disableDiscount(coachingCenterId).unwrap();

      setDiscountEnabled(false);

      showMessage("Coaching center discount disabled successfully.", "success");

      refetch();
    } catch (err) {
      showMessage(
        err?.data?.message || err?.error || "Unable to disable discount.",
        "error",
      );
    }
  };

  // =====================================================
  // Delete Billing Settings
  // =====================================================

  const handleDeleteBillingSettings = async () => {
    setShowDeleteConfirm(false);

    clearMessage();

    try {
      await deleteBillingSettings(coachingCenterId).unwrap();

      showMessage("Billing settings deleted successfully.", "success");

      navigate(-1);
    } catch (err) {
      showMessage(
        err?.data?.message ||
          err?.error ||
          "Unable to delete billing settings.",
        "error",
      );
    }
  };

  // =====================================================
  // Loading
  // =====================================================

  if (isLoading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <div className="text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600 mx-auto" />

          <p className="mt-3 text-gray-500">Loading billing settings...</p>
        </div>
      </div>
    );
  }

  // =====================================================
  // Error
  // =====================================================

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-6 text-red-700">
        <h3 className="font-semibold text-lg">
          Unable to load billing settings
        </h3>

        <p className="mt-2">
          {error?.data?.message ||
            error?.error ||
            "Something went wrong while loading billing settings."}
        </p>

        <button
          onClick={() => refetch()}
          className="
            mt-4
            rounded-lg
            bg-red-600
            px-4
            py-2
            text-white
            hover:bg-red-700
          "
        >
          Try Again
        </button>
      </div>
    );
  }

  // =====================================================
  // Render
  // =====================================================

  return (
    <div className="pb-10">
      {/* =====================================================
          Header
      ===================================================== */}

      <div className="mb-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              Coaching Center Billing Settings
            </h1>

            <p className="mt-1 text-gray-500">
              Configure billing and discount settings for this coaching center.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="
              rounded-lg
              border
              border-gray-300
              bg-white
              px-4
              py-2
              text-sm
              font-medium
              text-gray-700
              hover:bg-gray-50
            "
          >
            Back
          </button>
        </div>
      </div>

      {/* =====================================================
          Message
      ===================================================== */}

      {message && (
        <div
          className={`mb-6 rounded-lg border px-4 py-3 ${
            messageType === "success"
              ? "border-green-200 bg-green-50 text-green-700"
              : "border-red-200 bg-red-50 text-red-700"
          }`}
        >
          <div className="flex items-center justify-between gap-4">
            <span>{message}</span>

            <button type="button" onClick={clearMessage} className="font-bold">
              ×
            </button>
          </div>
        </div>
      )}

      {/* =====================================================
          Coaching Center Information
      ===================================================== */}

      <div className="mb-6 rounded-xl bg-white shadow">
        <div className="border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-800">
            Coaching Center
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-3">
          <InfoItem label="Name" value={coachingCenter?.name} />

          <InfoItem label="Email" value={coachingCenter?.email} />

          <InfoItem label="Phone" value={coachingCenter?.phone} />
        </div>
      </div>

      {/* =====================================================
          Billing Status
      ===================================================== */}

      <div className="mb-6 rounded-xl bg-white shadow">
        <div className="flex flex-col gap-4 border-b px-6 py-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              Billing Status
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Control whether billing settings are active for this coaching
              center.
            </p>
          </div>

          <StatusBadge
            active={isActive}
            activeText="Billing Active"
            inactiveText="Billing Disabled"
          />
        </div>

        <div className="flex flex-wrap gap-3 p-6">
          {isActive ? (
            <button
              type="button"
              onClick={() => setShowDisableConfirm(true)}
              disabled={isDisabling}
              className="
                rounded-lg
                bg-red-600
                px-4
                py-2
                text-sm
                font-medium
                text-white
                hover:bg-red-700
                disabled:cursor-not-allowed
                disabled:opacity-50
              "
            >
              {isDisabling ? "Disabling..." : "Disable Billing"}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleEnableBilling}
              disabled={isEnabling}
              className="
                rounded-lg
                bg-green-600
                px-4
                py-2
                text-sm
                font-medium
                text-white
                hover:bg-green-700
                disabled:cursor-not-allowed
                disabled:opacity-50
              "
            >
              {isEnabling ? "Enabling..." : "Enable Billing"}
            </button>
          )}
        </div>
      </div>

      {/* =====================================================
          Billing Settings Form
      ===================================================== */}

      <form onSubmit={handleSubmit}>
        <div className="mb-6 rounded-xl bg-white shadow">
          <div className="border-b px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-800">
              Billing Configuration
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Configure the discount applied to this coaching center's billing.
            </p>
          </div>

          <div className="space-y-6 p-6">
            {/* Billing Active */}

            <div className="flex items-center justify-between rounded-lg border bg-gray-50 p-4">
              <div>
                <p className="font-medium text-gray-800">
                  Billing Settings Active
                </p>

                <p className="mt-1 text-sm text-gray-500">
                  Enable billing configuration for this coaching center.
                </p>
              </div>

              <Toggle checked={isActive} onChange={setIsActive} />
            </div>

            {/* Discount Enabled */}

            <div className="flex items-center justify-between rounded-lg border bg-gray-50 p-4">
              <div>
                <p className="font-medium text-gray-800">Enable Discount</p>

                <p className="mt-1 text-sm text-gray-500">
                  Apply a coaching-center-specific discount.
                </p>
              </div>

              <Toggle checked={discountEnabled} onChange={setDiscountEnabled} />
            </div>

            {/* Discount Configuration */}

            {discountEnabled && (
              <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-5">
                <h3 className="mb-5 font-semibold text-gray-800">
                  Discount Configuration
                </h3>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  {/* Discount Type */}

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Discount Type
                    </label>

                    <select
                      value={discountType}
                      onChange={(e) => setDiscountType(e.target.value)}
                      className="
                        w-full
                        rounded-lg
                        border
                        border-gray-300
                        bg-white
                        px-3
                        py-2.5
                        focus:border-blue-500
                        focus:outline-none
                        focus:ring-2
                        focus:ring-blue-200
                      "
                    >
                      <option value="percentage">Percentage</option>

                      <option value="fixed">Fixed Amount</option>
                    </select>
                  </div>

                  {/* Discount Value */}

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Discount Value
                    </label>

                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        max={discountType === "percentage" ? "100" : undefined}
                        step="0.01"
                        value={discountValue}
                        onChange={(e) => setDiscountValue(e.target.value)}
                        placeholder={
                          discountType === "percentage" ? "10" : "500"
                        }
                        className="
                          w-full
                          rounded-lg
                          border
                          border-gray-300
                          bg-white
                          px-3
                          py-2.5
                          pr-16
                          focus:border-blue-500
                          focus:outline-none
                          focus:ring-2
                          focus:ring-blue-200
                        "
                      />

                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                        {discountType === "percentage" ? "%" : "₹"}
                      </span>
                    </div>

                    {discountType === "percentage" && (
                      <p className="mt-1 text-xs text-gray-500">
                        Maximum allowed: 100%
                      </p>
                    )}
                  </div>

                  {/* Maximum Amount */}

                  {discountType === "percentage" && (
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Maximum Discount Amount
                      </label>

                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={maxAmount}
                        onChange={(e) => setMaxAmount(e.target.value)}
                        placeholder="Optional"
                        className="
                          w-full
                          rounded-lg
                          border
                          border-gray-300
                          bg-white
                          px-3
                          py-2.5
                          focus:border-blue-500
                          focus:outline-none
                          focus:ring-2
                          focus:ring-blue-200
                        "
                      />

                      <p className="mt-1 text-xs text-gray-500">
                        Optional maximum amount for a percentage discount.
                      </p>
                    </div>
                  )}

                  {/* Reason */}

                  <div
                    className={discountType === "fixed" ? "md:col-span-2" : ""}
                  >
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Discount Reason
                    </label>

                    <input
                      type="text"
                      maxLength="250"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Reason for this discount"
                      className="
                        w-full
                        rounded-lg
                        border
                        border-gray-300
                        bg-white
                        px-3
                        py-2.5
                        focus:border-blue-500
                        focus:outline-none
                        focus:ring-2
                        focus:ring-blue-200
                      "
                    />
                  </div>

                  {/* Valid From */}

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Valid From
                    </label>

                    <input
                      type="date"
                      value={validFrom}
                      onChange={(e) => setValidFrom(e.target.value)}
                      className="
                        w-full
                        rounded-lg
                        border
                        border-gray-300
                        bg-white
                        px-3
                        py-2.5
                        focus:border-blue-500
                        focus:outline-none
                        focus:ring-2
                        focus:ring-blue-200
                      "
                    />
                  </div>

                  {/* Valid Until */}

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Valid Until
                    </label>

                    <input
                      type="date"
                      min={validFrom || undefined}
                      value={validUntil}
                      onChange={(e) => setValidUntil(e.target.value)}
                      className="
                        w-full
                        rounded-lg
                        border
                        border-gray-300
                        bg-white
                        px-3
                        py-2.5
                        focus:border-blue-500
                        focus:outline-none
                        focus:ring-2
                        focus:ring-blue-200
                      "
                    />
                  </div>
                </div>

                {/* Discount Preview */}

                <div className="mt-5 rounded-lg border border-blue-200 bg-white p-4">
                  <p className="text-sm font-semibold text-gray-700">
                    Discount Summary
                  </p>

                  <div className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
                    <div>
                      <span className="text-gray-500">Type</span>

                      <p className="font-medium text-gray-800">
                        {discountType === "percentage"
                          ? "Percentage"
                          : "Fixed Amount"}
                      </p>
                    </div>

                    <div>
                      <span className="text-gray-500">Value</span>

                      <p className="font-medium text-gray-800">
                        {discountType === "percentage"
                          ? `${Number(discountValue || 0)}%`
                          : formatCurrency(discountValue)}
                      </p>
                    </div>

                    <div>
                      <span className="text-gray-500">Maximum</span>

                      <p className="font-medium text-gray-800">
                        {discountType === "percentage" && maxAmount !== ""
                          ? formatCurrency(maxAmount)
                          : "No limit"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Form Actions */}

            <div className="flex flex-col gap-3 border-t pt-6 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  if (settings) {
                    setIsActive(Boolean(settings.isActive));

                    const discount = settings.discount || {};

                    setDiscountEnabled(Boolean(discount.enabled));

                    setDiscountType(discount.type || "percentage");

                    setDiscountValue(
                      discount.value !== null && discount.value !== undefined
                        ? String(discount.value)
                        : "",
                    );

                    setMaxAmount(
                      discount.maxAmount !== null &&
                        discount.maxAmount !== undefined
                        ? String(discount.maxAmount)
                        : "",
                    );

                    setReason(discount.reason || "");

                    setValidFrom(formatDateForInput(discount.validFrom));

                    setValidUntil(formatDateForInput(discount.validUntil));
                  }

                  clearMessage();
                }}
                className="
                  rounded-lg
                  border
                  border-gray-300
                  bg-white
                  px-5
                  py-2.5
                  text-sm
                  font-medium
                  text-gray-700
                  hover:bg-gray-50
                "
              >
                Reset
              </button>

              <button
                type="submit"
                disabled={isUpdating}
                className="
                  rounded-lg
                  bg-blue-600
                  px-5
                  py-2.5
                  text-sm
                  font-medium
                  text-white
                  hover:bg-blue-700
                  disabled:cursor-not-allowed
                  disabled:opacity-50
                "
              >
                {isUpdating ? "Saving..." : "Save Billing Settings"}
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* =====================================================
          Current Discount
      ===================================================== */}

      <div className="mb-6 rounded-xl bg-white shadow">
        <div className="border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-800">
            Current Discount
          </h2>
        </div>

        <div className="p-6">
          {settings?.discount?.enabled ? (
            <div>
              <div className="mb-5 flex items-center justify-between">
                <StatusBadge
                  active
                  activeText="Discount Active"
                  inactiveText="Discount Disabled"
                />

                <button
                  type="button"
                  onClick={() => setShowDiscountDisableConfirm(true)}
                  disabled={isDisablingDiscount}
                  className="
                    rounded-lg
                    border
                    border-red-300
                    px-4
                    py-2
                    text-sm
                    font-medium
                    text-red-600
                    hover:bg-red-50
                    disabled:opacity-50
                  "
                >
                  {isDisablingDiscount ? "Disabling..." : "Disable Discount"}
                </button>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
                <InfoItem
                  label="Type"
                  value={
                    settings.discount.type === "percentage"
                      ? "Percentage"
                      : "Fixed Amount"
                  }
                />

                <InfoItem
                  label="Value"
                  value={
                    settings.discount.type === "percentage"
                      ? `${settings.discount.value || 0}%`
                      : formatCurrency(settings.discount.value)
                  }
                />

                <InfoItem
                  label="Maximum Discount"
                  value={
                    settings.discount.type === "percentage" &&
                    settings.discount.maxAmount !== null
                      ? formatCurrency(settings.discount.maxAmount)
                      : "No limit"
                  }
                />

                <InfoItem
                  label="Reason"
                  value={settings.discount.reason || "-"}
                />

                <InfoItem
                  label="Valid From"
                  value={formatDate(settings.discount.validFrom)}
                />

                <InfoItem
                  label="Valid Until"
                  value={formatDate(settings.discount.validUntil)}
                />
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
              <div className="flex items-center gap-3">
                <span className="h-3 w-3 rounded-full bg-gray-400" />

                <div>
                  <p className="font-medium text-gray-800">Discount Disabled</p>

                  <p className="mt-1 text-sm text-gray-500">
                    No coaching-center discount is currently enabled.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          Settings Metadata
      ===================================================== */}

      {settings && (
        <div className="mb-6 rounded-xl bg-white shadow">
          <div className="border-b px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-800">
              Settings Information
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-5 p-6 md:grid-cols-3">
            <InfoItem label="Created" value={formatDate(settings.createdAt)} />

            <InfoItem
              label="Last Updated"
              value={formatDate(settings.updatedAt)}
            />

            <InfoItem
              label="Settings Status"
              value={settings.isActive ? "Active" : "Disabled"}
            />
          </div>
        </div>
      )}

      {/* =====================================================
          Danger Zone
      ===================================================== */}

      <div className="rounded-xl border border-red-200 bg-white shadow">
        <div className="border-b border-red-100 px-6 py-4">
          <h2 className="font-semibold text-red-700">Danger Zone</h2>
        </div>

        <div className="flex flex-col gap-4 p-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="font-medium text-gray-800">Delete Billing Settings</p>

            <p className="mt-1 text-sm text-gray-500">
              This removes the billing settings document for this coaching
              center. Existing invoices will not be affected.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting}
            className="
              rounded-lg
              border
              border-red-600
              px-4
              py-2
              text-sm
              font-medium
              text-red-600
              hover:bg-red-600
              hover:text-white
              disabled:opacity-50
            "
          >
            {isDeleting ? "Deleting..." : "Delete Settings"}
          </button>
        </div>
      </div>

      {/* =====================================================
          Disable Billing Confirmation
      ===================================================== */}

      {showDisableConfirm && (
        <ConfirmationModal
          title="Disable Billing Settings?"
          message="Billing settings will be disabled for this coaching center. You can enable them again later."
          confirmText="Disable Billing"
          confirmClass="bg-red-600 hover:bg-red-700"
          onCancel={() => setShowDisableConfirm(false)}
          onConfirm={handleDisableBilling}
          loading={isDisabling}
        />
      )}

      {/* =====================================================
          Disable Discount Confirmation
      ===================================================== */}

      {showDiscountDisableConfirm && (
        <ConfirmationModal
          title="Disable Discount?"
          message="The current coaching center discount will be disabled. The discount configuration will remain saved."
          confirmText="Disable Discount"
          confirmClass="bg-red-600 hover:bg-red-700"
          onCancel={() => setShowDiscountDisableConfirm(false)}
          onConfirm={handleDisableDiscount}
          loading={isDisablingDiscount}
        />
      )}

      {/* =====================================================
          Delete Confirmation
      ===================================================== */}

      {showDeleteConfirm && (
        <ConfirmationModal
          title="Delete Billing Settings?"
          message="This will permanently remove the billing settings document for this coaching center. Existing invoices will not be affected."
          confirmText="Delete Settings"
          confirmClass="bg-red-600 hover:bg-red-700"
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteBillingSettings}
          loading={isDeleting}
        />
      )}
    </div>
  );
};

// =====================================================
// Info Item
// =====================================================

const InfoItem = ({ label, value }) => {
  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>

      <p className="mt-1 font-semibold text-gray-800 break-words">
        {value || "-"}
      </p>
    </div>
  );
};

// =====================================================
// Status Badge
// =====================================================

const StatusBadge = ({
  active,
  activeText = "Active",
  inactiveText = "Disabled",
}) => {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${
        active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
      }`}
    >
      <span
        className={`h-2 w-2 rounded-full ${
          active ? "bg-green-500" : "bg-gray-400"
        }`}
      />

      {active ? activeText : inactiveText}
    </span>
  );
};

// =====================================================
// Toggle
// =====================================================

const Toggle = ({ checked, onChange }) => {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
        checked ? "bg-blue-600" : "bg-gray-300"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-5" : "translate-x-0.5"
        } mt-0.5`}
      />
    </button>
  );
};

// =====================================================
// Confirmation Modal
// =====================================================

const ConfirmationModal = ({
  title,
  message,
  confirmText,
  confirmClass,
  onCancel,
  onConfirm,
  loading,
}) => {
  return (
    <div
      className="
        fixed
        inset-0
        z-50
        flex
        items-center
        justify-center
        bg-black/50
        p-4
      "
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
        <div className="border-b px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm leading-6 text-gray-600">{message}</p>
        </div>

        <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="
              rounded-lg
              border
              border-gray-300
              bg-white
              px-4
              py-2
              text-sm
              font-medium
              text-gray-700
              hover:bg-gray-50
              disabled:opacity-50
            "
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`
              rounded-lg
              px-4
              py-2
              text-sm
              font-medium
              text-white
              disabled:cursor-not-allowed
              disabled:opacity-50
              ${confirmClass}
            `}
          >
            {loading ? "Please wait..." : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CoachingCenterBillingSettingsScreen;
